import { useCallback, useEffect, useRef, useState } from "react";
import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent, triggerPageExit } from "@tm/insurance/analytics/xdm";
import { replaceHistory, replaceHistoryRemoveParams } from "@tm/insurance/utils";
import { PARTNER } from "@tm/isomorphic-utils";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { errorTrackEvent } from "../../shared/MwebAnalytics";
import { convertDateToUnix, convertUnixToDate, getIndiaTodayDate } from "../../../../utils";
import { navigateRecentSearchNextPage } from "../../../../utils/recentSearchNavigation";
import type { Country, Segment, PricingView } from "../../CommonLandingMweb.types";
import { COMMON_LANDING_CONSTANTS } from "../../constants";
import {
  replaceHistoryPlanTypeAndSegment,
  replaceHistoryRemovePlanTypeAndSegment,
} from "./urlUtils";

interface FormActionsParams {
  resolvedTripOptionId: string | null;
  currentTripOption: any;
  selectedPlanSegment: any;
  pricingViewId: string | null;
  fromDate: Date;
  toDate: Date;
  travellerCount: number;
  selectedCountriesCount: number;
  segments: Segment[];
  travellerMin?: number;
  travellerMax?: number;
  travCountError?: string;
  setSelectedTripOptionId: (id: string | null) => void;
  setSelectedTripOption: (opt: any) => void;
  setSelectedCountries: (c: Country[]) => void;
  setFromDate: (d: Date) => void;
  setToDate: (d: Date) => void;
  setTravellerCount: (n: number) => void;
  setPlanSegmentId: (id: string | null) => void;
  setPricingViewId: (id: string | null) => void;
  onCountryChange?: (countries: Country[]) => void;
  onDateChange?: (from: Date, to: Date) => void;
  onTravellerChange?: (count: number) => void;
  onSegmentChange?: (segments: Segment[]) => void;
  onTripOptionChange?: (id: string | null) => void;
  onCTAPress?: () => void;
}

export function useTravelInsuranceFormActions({
  resolvedTripOptionId,
  currentTripOption,
  selectedPlanSegment,
  pricingViewId,
  fromDate,
  toDate,
  travellerCount,
  selectedCountriesCount,
  segments,
  setSelectedTripOptionId,
  setSelectedTripOption,
  setSelectedCountries,
  setFromDate,
  setToDate,
  setTravellerCount,
  setPlanSegmentId,
  setPricingViewId,
  onCountryChange,
  onDateChange,
  onTravellerChange,
  onSegmentChange,
  onTripOptionChange,
  onCTAPress,
  travellerMin = 1,
  travellerMax = 20,
  travCountError,
}: FormActionsParams) {
  const analytics = getAnalytics();

  const [formCountryErrorMsg, setFormCountryErrorMsg] = useState("");
  const [formDateErrorMsg, setFormDateErrorMsg] = useState("");
  const [formTravellerErrorMsg, setFormTravellerErrorMsg] = useState("");
  const [formValidationToastMsg, setFormValidationToastMsg] = useState("");
  const [exploreLoading, setExploreLoading] = useState(false);
  const [segmentModalHandled, setSegmentModalHandled] = useState(false);
  const [isSegmentModalShown, setIsSegmentModalShown] = useState(false);
  const [isTripOptionModalShown, setIsTripOptionModalShown] = useState(false);
  const [tripOptionModalHandled, setTripOptionModalHandled] = useState(false);

  const validationToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (validationToastTimeoutRef.current) clearTimeout(validationToastTimeoutRef.current);
  }, []);

  useEffect(() => { if (selectedCountriesCount > 0) setFormCountryErrorMsg(""); }, [selectedCountriesCount]);
  useEffect(() => { if (fromDate && toDate) setFormDateErrorMsg(""); }, [fromDate, toDate]);
  useEffect(() => {
    if (travellerCount >= travellerMin && travellerCount <= travellerMax) {
      setFormTravellerErrorMsg("");
    }
  }, [travellerCount]);

  useEffect(() => { setTripOptionModalHandled(false); setIsTripOptionModalShown(false); }, [resolvedTripOptionId]);
  useEffect(() => { setSegmentModalHandled(false); setIsSegmentModalShown(false); }, [selectedPlanSegment?.id]);

  const handleOptionSelect = useCallback((option: any) => {
    setSelectedTripOptionId(option.id);
    setSelectedTripOption(option);
    setPlanSegmentId(null);
    setPricingViewId(null);
    onTripOptionChange?.(option.id);
    if (typeof window === "undefined") return;
    replaceHistory("tripOption", option.id);
    const segList = option?.planTypes?.value as Segment[] | undefined;
    const firstSeg = segList?.find((s) => s.isSelected) ?? segList?.[0];
    if (firstSeg?.id) {
      replaceHistoryPlanTypeAndSegment(firstSeg.id);
      const next = (segList || []).map((item) => ({ ...item, isSelected: item.id === firstSeg.id }));
      onSegmentChange?.(next);
    } else {
      replaceHistoryRemovePlanTypeAndSegment();
    }
    const pricingList = option?.pricingView?.value as PricingView[] | undefined;
    const firstPricing = pricingList?.find((p) => p.isSelected) ?? pricingList?.[0];
    if (firstPricing?.id) { setPricingViewId(firstPricing.id); replaceHistory("pricingView", firstPricing.id); }
    else { replaceHistoryRemoveParams("pricingView"); }
  }, [setSelectedTripOptionId, setSelectedTripOption, setPlanSegmentId, setPricingViewId, onTripOptionChange, onSegmentChange]);

  const handleCountryChange = useCallback((countries: Country[]) => {
    setSelectedCountries(countries);
    onCountryChange?.(countries);
    if (typeof window === "undefined") return;
    const codes = countries.map((c) => c.code).filter(Boolean).join(",");
    if (codes) replaceHistory("countries", codes);
    else replaceHistoryRemoveParams("countries");
  }, [setSelectedCountries, onCountryChange]);

  const handleDateChange = useCallback((start: Date, end: Date) => {
    setFromDate(start);
    setToDate(end);
    onDateChange?.(start, end);
    if (typeof window === "undefined") return;
    replaceHistory("startDate", String(convertDateToUnix(start)));
    replaceHistory("endDate", String(convertDateToUnix(end)));
  }, [setFromDate, setToDate, onDateChange]);

  const handleTravellerChange = useCallback((count: number) => {
    setTravellerCount(count);
    onTravellerChange?.(count);

    if (count >= travellerMin && count <= travellerMax) {
      setFormTravellerErrorMsg("");
    } else if (travCountError) {
      setFormTravellerErrorMsg(travCountError);
    }

    if (typeof window === "undefined") return;
    replaceHistory("travellers", String(count));
  }, [setTravellerCount, onTravellerChange, travellerMin, travellerMax, travCountError]);

  const handleSegmentChange = useCallback((segment: Segment) => {
    setPlanSegmentId(segment.id);
    const nextSegments = segments.map((item) => ({ ...item, isSelected: item.id === segment.id }));
    onSegmentChange?.(nextSegments);
    if (typeof window === "undefined") return;
    if (segment.id) replaceHistoryPlanTypeAndSegment(segment.id);
    else replaceHistoryRemovePlanTypeAndSegment();
  }, [segments, setPlanSegmentId, onSegmentChange]);

  const handlePricingViewChange = useCallback((pricing: Segment) => {
    setPricingViewId(pricing.id);
    if (typeof window === "undefined") return;
    if (pricing.id) replaceHistory("pricingView", pricing.id);
    else replaceHistoryRemoveParams("pricingView");
  }, [setPricingViewId]);

  const handleExplorePlansClick = useCallback(async () => {
    if (typeof window === "undefined") return;

    setFormCountryErrorMsg("");
    setFormDateErrorMsg("");
    setFormTravellerErrorMsg("");
    setFormValidationToastMsg("");

    const searchParams = new URLSearchParams(window.location.search);
    const countrySelectErrMsg =
      currentTripOption?.countryList?.countrySelectErrMsg ||
      currentTripOption?.countryList?.error_message ||
      "Please select a country";

    let countries = searchParams.get("countries");
    const tripOptionFromUrl = searchParams.get("tripOption") || resolvedTripOptionId || "";

    if (tripOptionFromUrl === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID && countries) {
      countries = (countries.includes("USA") || countries.includes("CAN") || countries.includes("WW")) ? "WW" : "WWEUC";
    }

    const countryCodes = countries ? countries.split(",").map((c) => c.trim()).filter(Boolean) : [];

    if (!countryCodes.length || !countryCodes[0]) {
      setFormCountryErrorMsg(countrySelectErrMsg);
      analytics?.trackCommonLandingEvents?.({ actionType: "error_shown", payload: countrySelectErrMsg });
      errorTrackEvent({ error_details_list: [{ message: countrySelectErrMsg, source: "country_select", severity: "P3" }] });
    }

    const planTypeId = searchParams.get("planType") || searchParams.get("segment") || selectedPlanSegment?.id || "";

    if (planTypeId === "STUDENT" && countryCodes[0] === "IN") {
      const msg = "We do not have domestic student plans, please select another country to see student plans";
      setFormValidationToastMsg(msg);
      if (validationToastTimeoutRef.current) clearTimeout(validationToastTimeoutRef.current);
      validationToastTimeoutRef.current = setTimeout(() => { setFormValidationToastMsg(""); validationToastTimeoutRef.current = null; }, 3000);
      analytics?.trackCommonLandingEvents?.({ actionType: "error_shown", payload: msg });
      errorTrackEvent({ error_details_list: [{ message: msg, source: "student_plan", severity: "P3" }] });
      return;
    }

    replaceHistory("segment", planTypeId || "");

    const sd = searchParams.get("startDate");
    const ed = searchParams.get("endDate");
    const tv = searchParams.get("travellers");

    const rangeFrom = sd && !Number.isNaN(parseInt(sd, 10)) ? convertUnixToDate(parseInt(sd, 10)) : fromDate;
    const rangeTo = ed && !Number.isNaN(parseInt(ed, 10)) ? convertUnixToDate(parseInt(ed, 10)) : toDate;

    const hasValidDates = rangeFrom instanceof Date && !Number.isNaN(rangeFrom.getTime()) && rangeTo instanceof Date && !Number.isNaN(rangeTo.getTime());
    if (!hasValidDates) setFormDateErrorMsg("Please select start and end dates");

    const travellerCountNum = tv ? parseInt(tv, 10) : travellerCount;
    const hasValidTravellers = !Number.isNaN(travellerCountNum) && travellerCountNum >= travellerMin && travellerCountNum <= travellerMax;
    if (!hasValidTravellers) {
      setFormTravellerErrorMsg(
        travCountError || `Please select number of travellers between ${travellerMin} and ${travellerMax}`
      );
    }

    const canProceed = countryCodes.length > 0 && Boolean(countryCodes[0]) && hasValidDates && hasValidTravellers;

    if (canProceed) {
      if (tripOptionFromUrl && currentTripOption?.modal && !tripOptionModalHandled) {
        setIsTripOptionModalShown(true);
        setTripOptionModalHandled(true);
        return;
      }
      if (selectedPlanSegment?.modal && !segmentModalHandled) {
        setIsSegmentModalShown(true);
        setSegmentModalHandled(true);
        return;
      }

      onCTAPress?.();
      analytics?.trackCommonLandingEvents({ actionType: "explore_plan_click" });
      trackEvent({ event_detail: { event_type: "action", event_name: "component_cta-clicked", event_value: "explore_plan_click" } });
      triggerPageExit();

      const selectedSegmentIdV2 = tripOptionFromUrl === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID ? "MULTITRIP" : planTypeId;

      const requestData = {
        query: { itineraryId: searchParams.get("itineraryId") || "", source: "standalone_regular" },
        partner: PARTNER.MYPARTNER,
        userSearchIntent: {
          country: countryCodes,
          departureDateTime: convertDateToUnix(rangeFrom || getIndiaTodayDate()),
          arrivalDateTime: convertDateToUnix(rangeTo || rangeFrom || getIndiaTodayDate()),
          numberOfTravellers: travellerCountNum,
          ...(selectedSegmentIdV2 && { segment: selectedSegmentIdV2 }),
          ...(planTypeId && { planType: planTypeId }),
          ...(pricingViewId && { pricingView: pricingViewId }),
          tripOption: tripOptionFromUrl,
        },
      };

      setExploreLoading(true);
      try {
        storePageTransitionStart("landing");
        await navigateRecentSearchNextPage(requestData);
      } finally {
        setExploreLoading(false);
      }
    }

    analytics?.trackCommonLandingEvents({ actionType: "explore_clicked" });
  }, [
    analytics, currentTripOption, selectedPlanSegment, resolvedTripOptionId,
    fromDate, toDate, travellerCount, pricingViewId,
    tripOptionModalHandled, segmentModalHandled, onCTAPress,
  ]);

  return {
    formCountryErrorMsg,
    formDateErrorMsg,
    formTravellerErrorMsg,
    formValidationToastMsg,
    exploreLoading,
    isSegmentModalShown,
    setIsSegmentModalShown,
    setSegmentModalHandled,
    isTripOptionModalShown,
    setIsTripOptionModalShown,
    setTripOptionModalHandled,
    handleOptionSelect,
    handleCountryChange,
    handleDateChange,
    handleTravellerChange,
    handleSegmentChange,
    handlePricingViewChange,
    handleExplorePlansClick,
  };
}
